import React, { useContext, useEffect, useRef, useState } from "react";
import { PlayCircle, PauseCircle, Play, Pause } from "react-feather";

import { AppTheme } from "../helper/enums";
import { AppContext } from "../provider/AppContextProvider";
import X from "../public/icons/x.svg";
import { formatTimestampForPlayer } from "../helper/timeHelper";

function formatTime(duration, maxDuration) {
  let sec = Math.floor(duration);
  let min = Math.floor(sec / 60);
  if (maxDuration >= 3600) {
    min = min % 60;
  }
  let hou = Math.floor(duration / 3660);
  let hours = hou >= 10 ? hou : "0" + hou;
  let minutes = min >= 10 ? min : "0" + min;
  sec = Math.floor(sec % 60);
  let seconds = sec >= 10 ? sec : "0" + sec;

  if (maxDuration >= 3600) {
    return hours + ":" + minutes + ":" + seconds;
  }
  return minutes + ":" + seconds;
}

function AudioPlayer() {
  const { audioPlayer, setAudioPlayer } = useContext(AppContext);
  const audio = useRef<HTMLAudioElement | null>(null);
  const [currenTimestamp, setCurrenTimestamp] = useState("00:00");
  const [duration, setDuration] = useState("00:00");

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // TODO:Emanuel Progress Bar einbauen
  const showProgressBar = true;

  function handleTimeUpdate(event) {
    if (audio.current && audio.current.duration >= 0) {
      setDuration(formatTime(audio.current.duration, audio.current.duration));
      setCurrenTimestamp(
        formatTime(audio.current.currentTime, audio.current?.duration ?? 0)
      );
    } else {
      setCurrenTimestamp(formatTime(audio.current?.currentTime ?? 0, 0));
    }
  }

  function handleWaiting(event) {
    setIsLoading(true);
  }

  function handlePlaying(event) {
    if (isLoading) {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (audioPlayer) {
      if (audioPlayer.playing) {
        audio.current?.play();
      } else {
        audio.current?.pause();
      }
    }
  }, [audioPlayer]);

  function playerClose() {
    setAudioPlayer(null);
  }

  useEffect(() => {
    if (audio && audio.current && audio.current.duration >= 0) {
      setCurrenTimestamp(formatTime(0, audio.current.duration));
      setDuration(formatTime(audio.current.duration, audio.current.duration));
    }
  }, [audio]);

  const [played, setPlayed] = useState<number>(0.33);
  const [loaded, setLoaded] = useState<number>(0.75);

  useEffect(() => {}, [played]);

  function renderIcon() {
    if (isLoading) {
      return <span className="spinner spinner--on-primary"></span>;
    } else if (audioPlayer && audioPlayer.playing) {
      return <span className="icon-button_pause pause-button"></span>;
    } else {
      return <span className="icon-button_play play-button"></span>;
    }
  }

  const changeProgress = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    var progressBar = event.currentTarget as HTMLDivElement;

    if (progressBar) {
      const rect = progressBar.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const totalWidth = progressBar.clientWidth;
      const clickPercentage = clickX / totalWidth;

      setAudioTime((audio.current?.duration ?? 0) * clickPercentage);
    }
  };

  const setAudioTime = (seconds: number) => {
    if (audio.current && audio.current.duration > 0) {
      audio.current.currentTime = seconds;
    }
  };

  return (
    <React.Fragment>
      {audioPlayer && (
        <div
          className={`
                podcast-player 
                ${audioPlayer != null ? "podcast-player--visible" : ""}
                ${isLoading ? "podcast-player--is-loading" : ""}
                `}
        >
          <div className="podcast-player__top">
            <button
              className="podcast-player__close"
              onClick={() => {
                playerClose();
              }}
            >
              <X />
            </button>
            <button
              className="podcast-player__toggle-icon"
              onClick={() => {
                setAudioPlayer({
                  ...audioPlayer,
                  playing: !audioPlayer.playing,
                });
              }}
            >
              {process.env.NEXT_PUBLIC_APP_THEME == AppTheme.nn ? (
                <div className="podcast-icon">
                  {audioPlayer.playing ? <Pause /> : <Play />}
                </div>
              ) : (
                renderIcon()
              )}
            </button>

            <div className="podcast-player__meta">
              {showProgressBar && (
                <div className="podcast-player__meta__topline">
                  {audioPlayer.topline}
                </div>
              )}
              <div className="podcast-player__meta__title">
                {audioPlayer.title}
              </div>
              {!showProgressBar && (
                <div className="podcast-player__meta__status">
                  {currenTimestamp} / {duration}
                </div>
              )}
            </div>
          </div>

          {showProgressBar &&
          process.env.NEXT_PUBLIC_APP_THEME == AppTheme.nn ? (
            <ProgressBar
              min={0}
              value={audio.current?.currentTime || 0}
              max={audio.current?.duration || 0}
              onToggle={() => {
                setAudioPlayer({
                  ...audioPlayer,
                  playing: !audioPlayer.playing,
                });
              }}
              isPlay={audioPlayer.playing}
              setAudioTime={setAudioTime}
            />
          ) : (
            <div className="podcast-player__progress">
              <div className="podcast-player__progress__bg"></div>
              {loaded && (
                <div
                  className="podcast-player__progress__loaded"
                  style={{ width: `${1 * 100}%` }}
                ></div>
              )}
              {played && (
                <div
                  className="podcast-player__progress__played"
                  style={{ width: `${1 * 100}%` }}
                  onClick={(e) => changeProgress(e)}
                ></div>
              )}
              {played && (
                <div
                  className="podcast-player__progress__handle"
                  style={{
                    left: `${
                      ((audio.current?.currentTime || 0) /
                        (audio.current?.duration || 0)) *
                      100
                    }%`,
                  }}
                ></div>
              )}

              <div className="podcast-player__progress__display-played">
                {formatTimestampForPlayer(audio.current?.currentTime || 0)}
              </div>
              {/* remaining = length - played */}
              <div className="podcast-player__progress__display-remaining">
                {formatTimestampForPlayer(audio.current?.duration || 0)}
              </div>
            </div>
          )}

          <audio
            className="podcast-player__audio"
            ref={audio}
            src={audioPlayer.audioUrl}
            onTimeUpdate={handleTimeUpdate}
            onWaiting={handleWaiting}
            onPlaying={handlePlaying}
            controls
          ></audio>
        </div>
      )}
    </React.Fragment>
  );
}

interface ProgressBarProps {
  min: number;
  max: number;
  value: number;
  step?: number;
  isPlay?: boolean;
  onToggle: () => void;
  setAudioTime: (seconds: number) => void;
}

const ProgressBar = ({
  min,
  max,
  value,
  step = 1,
  onToggle,
  setAudioTime,
  isPlay = true,
}: ProgressBarProps) => {
  const progress = (+value / +max) * 100;

  return (
    <div className="nn-progressbar">
      <div className="range-wrapper">
        <p className="range-time">{formatTime(value, max)}</p>
        <input
          type="range"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e) => {
            setAudioTime(parseInt(e.target.value));
          }}
          style={{
            background: `linear-gradient(to right, rgb(218, 31, 61) ${progress}%, rgb(134, 134, 134) ${progress}%)`,
          }}
        />
      </div>
      <button onClick={onToggle} className="nn-progressbar__action">
        {isPlay ? <PauseCircle /> : <PlayCircle />}
      </button>
    </div>
  );
};

export default AudioPlayer;
